/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions*/

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Formik } from "formik";

import { GlobalAuthContext } from "store/context";
import { LoaderContext } from '@fitinsur/contexts';
import { useTextMessage } from '@fitinsur/hooks';
import { LineLabel } from './styles';

import { useGetAdminAccessRoleList } from "@fitinsur/services/AdminService";
import { useAccessRoleClone, useAccessRoleExpire, useAccessRoleRestore } from "@fitinsur/services/AccessRoleService";

import Colors from "constants/Colors";

import {
  Button,
  ButtonWithIcon,
  Text,
  ResponsiveTable,
  ListHeader,
  ListActions,
  ModalConfirmation
} from '@fitinsur/components';

import * as S from "./styles";

export function AccessRoleList({ permissions }) {
  const { getDescription } = useTextMessage();
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const history = useHistory();
  const location = useLocation();
  const formik = useRef();

  const columns = [
    {
      title: getDescription("DOMAINS", 780, { textFormat: 'uppercase' }),
      key: "roleName",
      sortKey: 'roleName',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) =>
        <LineLabel>
          {`${listItem.idRole} - ${listItem.roleName}`}
        </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 781, { textFormat: 'uppercase' }),
      key: "isExternalAccessRole",
      sortKey: 'isExternalAccessRole',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) =>
        <LineLabel>
          {listItem.isExternalAccessRole ? getDescription("DOMAINS", 800) : getDescription("DOMAINS", 801)}
        </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 782, { textFormat: 'uppercase' }),
      key: "groupsCount",
      sortKey: 'groupsCount',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) =>
        <LineLabel>
          {listItem.groupsCount ?? '-'}
        </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 783, { textFormat: 'uppercase' }),
      key: "action",
      minWidth: 8,
      maxWidth: 8,
      paddingRight: 5,
      component: (listItem) => <ListActions
        viewRoute={listItem.accessRoleExpireDate ? () => null : () => history.push(`/management/58/access-role/view/${listItem.idRole}`)}
        editRoute={listItem.accessRoleExpireDate ? () => null : () => history.push(`/management/58/access-role/edit/${listItem.idRole}`)}
        setShowModalConfirmationClone={() => {
          setCurrentRole(listItem.idRole);
          setShowModalConfirmationClone(true);
        }}
        setShowModalConfirmationExpire={() => {
          setCurrentRole(listItem.idRole);
          setIsRestore(listItem.accessRoleExpireDate);
          setShowModalConfirmationExpire(true);
        }}
        expired={listItem.accessRoleExpireDate}
      />
    },
  ];

  const initialFilterData = [
    { label: getDescription("DOMAINS", 780, { textFormat: 'uppercase' }), orderKey: 'roleName', key: 'roleName', type: 'text' },
    { label: getDescription("DOMAINS", 781, { textFormat: 'uppercase' }), orderKey: 'isExternalAccessRole', key: 'isExternalAccessRole', type: 'list' },
    // { label: getDescription("DOMAINS", 782, { textFormat: 'uppercase' }), orderKey: 'groupsCount', key: 'groupsCount', type: 'text' },
  ];

  const storedFilterOption = localStorage.getItem('access-role-filter');
  const storedStringFilterOption = localStorage.getItem('str-access-role-filter');
  let quotesUrl = '/management/58/access-role' + (storedStringFilterOption ? '?' + storedStringFilterOption : '');

  const [filterData, setFilterData] = useState(storedFilterOption ? JSON.parse(storedFilterOption) : initialFilterData);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listData, setListData] = useState([]);
  const [idOperationSelected, setIdOperationSelected] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [isRestore, setIsRestore] = useState(null);
  const [showModalConfirmationClone, setShowModalConfirmationClone] = useState(false);
  const [showModalConfirmationExpire, setShowModalConfirmationExpire] = useState(false);
  const [paginationControls, setPaginationControls] = useState({
    "currentPage": 1,
    "numberMaxRecords": null,
    "total": null,
    "totalPages": null,
    "numberRecords": null
  });
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const initialValues = {
    filter: {
      number: "",
      firstName: "",
      fullName: "",
      hierarchy: "",
      parent: "",
      holding: "",
      personStatus: "",
    },
  };

  const {
    loading: useGetAdminAccessRoleListLoading,
    request: useGetAdminAccessRoleListRequest,
  } = useGetAdminAccessRoleList();

  const {
    loading: useAccessRoleCloneLoading,
    status: useAccessRoleCloneStatus,
    request: useAccessRoleCloneRequest,
  } = useAccessRoleClone(currentRole);

  const {
    loading: useAccessRoleExpireLoading,
    status: useAccessRoleExpireStatus,
    request: useAccessRoleExpireRequest,
  } = useAccessRoleExpire(currentRole);

  const {
    loading: useAccessRoleRestoreLoading,
    status: useAccessRoleRestoreStatus,
    request: useAccessRoleRestoreRequest,
  } = useAccessRoleRestore(currentRole);

  async function getAccessRoleListData(resetFilter) {
    const refFilterData = resetFilter ? initialFilterData : filterData;
    useGetAdminAccessRoleListRequest(null, {
      page: paginationControls.currentPage,
      sortBy,
      ...(refFilterData
        .filter(v => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a
        }, {}))
    }).then((resp) => {
      const { data: { value: { roles, pagination } } } = resp;

      if (paginationControls.currentPage === 1) {
        setListData(roles);
      } else {
        setListData([...listData, ...roles]);
        setCurrentPage(paginationControls.currentPage);
      }

      setTotalPages(pagination?.totalPages);
    });
  }

  function handleSortList(key, direction) {
    if (!key || key === 'action' || key === 'status') return;
    direction = direction === 1 ? 'DESC' : 'ASC';
    let sort = { key, direction };
    setSortBy(sort);
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);
    getAccessRoleListData();
  }

  function handleFilter(resetFilter) {
    quotesUrl = '/management/58/access-role' + (localStorage.getItem('access-role-filter') ? '?' + localStorage.getItem('str-access-role-filter') : '')
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);

    if (resetFilter) {
      getAccessRoleListData(resetFilter);
      setSortBy({ key: null, direction: null });
      localStorage.removeItem('access-role-filter')
      localStorage.removeItem('str-access-role-filter')
      history.push(`/management/58/access-role`)
    } else {
      getAccessRoleListData();
      history.push(`${quotesUrl}`)
    }
  };

  function setNextPage(paginationControls) {
    let e = paginationControls;
    e.currentPage++;
    setPaginationControls(e);
    getAccessRoleListData();
  }

  useEffect(() => {
    getAccessRoleListData()
  }, []);

  useEffect(() => {
    updateIsLoadingStatus(
      useGetAdminAccessRoleListLoading ||
      useAccessRoleCloneLoading ||
      useAccessRoleExpireLoading ||
      useAccessRoleRestoreLoading
    );
  }, [
    useGetAdminAccessRoleListLoading,
    useAccessRoleCloneLoading,
    useAccessRoleExpireLoading,
    useAccessRoleRestoreLoading
  ]);

  useEffect(() => {
    if (
      useAccessRoleCloneStatus === 200
    ) {
      setShowModalConfirmationClone(false);
      setShowModalConfirmationExpire(false);
      window.location.reload();
    }
  }, [useAccessRoleCloneStatus]);

  useEffect(() => {
    if (
      useAccessRoleExpireStatus === 200
    ) {

      setListData((prev) => prev.map((item) => {
        if (item.idRole === currentRole) {
          item.accessRoleExpireDate = new Date();
        }
        return item;
      }));

      setShowModalConfirmationExpire(false);
    }
  }, [useAccessRoleExpireStatus]);

  useEffect(() => {
    if (
      useAccessRoleRestoreStatus === 200
    ) {

      setListData((prev) => prev.map((item) => {
        if (item.idRole === currentRole) {
          item.accessRoleExpireDate = null;
        }

        return item;
      }));

      setShowModalConfirmationExpire(false);
    }
  }, [useAccessRoleRestoreStatus]);

  return (
    <>
      <ButtonWithIcon
        onClick={() => history.push('/management/58/access-role/create')}
        icon={{ name: "plus-gray", width: '12px', height: '12px' }}
        title={getDescription("DOMAINS", 779, { withSkeleton: true, skeletonWidth: '100px', skeletonHeight: '16px' })}
      />

      <S.Content>
        <ModalConfirmation
          show={showModalConfirmationClone || showModalConfirmationExpire}
          text={showModalConfirmationClone ? getDescription("DOMAINS", 793) : !isRestore ? getDescription("DOMAINS", 803) : getDescription("DOMAINS", 840)}
          showTitle={false}
          textButtonCancel={getDescription("DOMAINS", 791)}
          textButtonConfirm={getDescription("DOMAINS", 792)}
          onConfirm={() => {
            if (showModalConfirmationClone) useAccessRoleCloneRequest({ idRole: currentRole });
            if (showModalConfirmationExpire && !isRestore) useAccessRoleExpireRequest({ idRole: currentRole });
            if (isRestore) useAccessRoleRestoreRequest({ idRole: currentRole });
          }
          }
          onCancel={() => {
            setCurrentRole(null)
            setShowModalConfirmationClone(false)
            setShowModalConfirmationExpire(false)
          }}
        />
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => (
            <Form>
              <ListHeader
                isAdmin
                filterData={filterData}
                setFilterData={setFilterData}
                onHandleFilter={(resetFilter) => handleFilter(resetFilter)}
                listNameSpace={'access-role-filter'}
                listUrl='/management/58/access-role'
                optionsExternal={[
                  { label: getDescription("DOMAINS", 800), value: 'true' },
                  { label: getDescription("DOMAINS", 801), value: 'false' }
                ]}
                idDomainList={996}
                idApplication={675}
                filterNamePrefix={getDescription("DOMAINS", 1074, { textFormat: 'uppercase' })}
                defaultFilterName={getDescription("DOMAINS", 1075, { textFormat: 'uppercase' })}
              />
              <>
                <ResponsiveTable
                  onSortTable={handleSortList}
                  formikRef={formik?.current}
                  columns={columns}
                  data={listData}
                />

                {!listData.length && (
                  <S.ButtonContainer style={{ minHeight: '450px' }}>
                    <Text style={{
                      fontWeight: 'bold',
                      color: Colors.GRAY[1],
                      margin: 'auto',
                      cursor: 'pointer',
                      marginTop: '10px'
                    }}
                      onClick={() => { }}
                    >
                      {getDescription("DOMAINS", 786)}
                    </Text>
                  </S.ButtonContainer>
                )}
              </>
            </Form>
          )}
        </Formik>

        <S.LoadContainer>
          {!listData?.length || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              {getDescription("DOMAINS", 784, { textFormat: 'uppercase' })}
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage(paginationControls)}>
              + {getDescription("DOMAINS", 785, { textFormat: 'uppercase' })}
            </Button>
          )}
        </S.LoadContainer>
      </S.Content>
    </>
  );
}
